<template>
  <div>
    <fieldset>
      <input
        type="hidden"
        :name="'vehicle-customisation'"
        :value="currentSelectionKey"
        :data-vv-scope="validationScope"
        v-validate.continues="'vehicle-customisation'"/>
      <input id="RangeId" name="rangeId" type="hidden" :value="searchQuery.rangeIds">
      <fieldset id="fuelType-wp" class="tla-form-group" v-if="bodystyle.length > 0">
        <label class="tla-form-group__label">
          {{fuelTypeText}}
        </label>
        <div v-if="questionLabels[0]">
          <label  class="tla-form-group__label"
            v-html="questionLabels[0]"></label>
        </div>
        <div class="tla-form-group__touchpoints">
          <form-touch-point v-for="(item, index) in fuelTypeOptions"
            :itemName="item"
            :index="index"
            :key="item"
            :collectionName="'fuelType'"
            :validationRules="''"
            :validationScope="validationScope"
            :selected="(fuelTypeOptions.length < 2) || item === searchQuery.fuelTypes"
            :itemText="item"
            :arrayLength="fuelTypeOptions.length"
            @fuelType="setFuelTypeChoice($event)">
          </form-touch-point>
        </div>
      </fieldset>

      <fieldset id="transmission-wp" class="tla-form-group" v-if="searchQuery.fuelTypes !== null">
        <label class="tla-form-group__label">
          {{transmissionText}}
        </label>
        <div v-if="questionLabels[1]">
          <label  class="tla-form-group__label"
            v-html="questionLabels[1]"></label>
        </div>
        <div class="tla-form-group__touchpoints">
          <form-touch-point v-for="(item, index) in transmissionOptions"
            :itemName="item"
            :index="index"
            :key="`${searchQuery.fuelTypes[0]}${item}`"
            :collectionName="'transmission'"
            :validationRules="''"
            :validationScope="validationScope"
            :selected="(transmissionOptions.length < 2) || item === searchQuery.transmissions"
            :itemText="item"
            :arrayLength="transmissionOptions.length"
            @transmission="setTransmissionChoice($event)">
          </form-touch-point>
        </div>
      </fieldset>

      <fieldset id="doors-wp" class="tla-form-group" v-if="searchQuery.transmissions !== null && !this.hideDoorsTouchpoint">
        <label class="tla-form-group__label">
          {{doorsText}}
        </label>
        <div v-if="questionLabels[2]">
          <label  class="tla-form-group__label"
            v-html="questionLabels[2]"></label>
        </div>
        <div class="tla-form-group__touchpoints">
          <form-touch-point v-for="(item, index) in doorOptions"
            :itemName="`${item}-doors`"
            :itemId="item"
            :collectionClassName="'doors'"
            :index="index"
            :key="`${searchQuery.fuelTypes[0]}${searchQuery.transmissions[0]}${item}`"
            :collectionName="'selectedDoors'"
            :validationRules="''"
            :validationScope="validationScope"
            :selected="(doorOptions.length < 2) || item === searchQuery.doors"
            :itemText="item"
            :arrayLength="doorOptions.length"
            @selectedDoors="setDoorChoice($event)">
          </form-touch-point>
        </div>
      </fieldset>

      <fieldset id="colour-wp" class="tla-form-group" v-if="!hideColourComponent && searchQuery.doors !== null && searchQuery.doors > 0">
        <label class="tla-form-group__label">
          {{colourTypeText}}
        </label>
        <div v-if="questionLabels[3]">
          <label  class="tla-form-group__label"
            v-html="questionLabels[3]"></label>
        </div>
        <div class="tla-form-group__touchpoints tla-form-group__touchpoints--spaced">
          <colour-touchpoint v-for="(item, index) in colourOptions" :key="`${item}${index}`"
            :id="`${item}${index}`"
            :text="item"
            :colour="item"
            :collectionName="'basicColour'"
            :validationScope="validationScope"
            :validationRules="'required'">
          </colour-touchpoint>
        </div>
      </fieldset>

      <fieldset
        id="derivative-wp"
        class="tla-form-group"
        v-if="!isTrim"
        v-show="showVehicleChoice && !hideDerivativeOptions">
        <label class="tla-form-group__label">
          Trim / Variant
        </label>
        <div v-if="questionLabels[4]">
          <label  class="tla-form-group__label"
            v-html="questionLabels[4]"></label>
        </div>
        <select
          class="tla-form-group__input tla-form-group__input--arrow"
          :class="getValidationClass('DerivativeId')"
          v-model="derivative"
          name="DerivativeId"
          :data-vv-scope="validationScope"
          v-validate:derivative="'required'">
          <option selected disabled value="null">
            Please select a trim / variant
          </option>
          <option
            v-for="derivative in derivatives"
            :value="derivative.derivativeId"
            :key="derivative.derivativeId">
            {{derivative.derivativeName}}
          </option>
        </select>
      </fieldset>
      <vehicle-trim ref="trimComponent" v-if="showVehicleChoice && isTrim" :validationScope="validationScope"></vehicle-trim>
    </fieldset>
  </div>
  </template>

<script lang="ts">
import VeeValidate, { Validator, FieldError } from 'vee-validate';
import {
  Component, Prop, Vue, Watch, Inject, Ref
} from 'vue-property-decorator';
import {
  State,
  Getter,
  Action,
  Mutation,
  namespace
} from 'vuex-class';
import VehicleLevel from '@/enums/VehicleLevel';
import SearchQuery from '@/models/SearchQuery';
import Derivative from '../../models/vehicle/derivative';
import { scrollService } from '../../services/ScrollService';
import { validationService } from '../../services/ValidationService';
import FormTouchPointComponent from '../ui/FormTouchPointComponent.vue';
import RadioButtonOptions from '../../models/RadioButtonOptions';
import KeyValue from '../../models/KeyValue';
import { tokenService } from '../../services/TokenService';
import ColourComponent from './ColourComponent.vue';
import VehicleTrimComponent from './VehicleTrimComponent.vue';
import IDerivative from '../../models/vehicle/interfaces/derivative.d';

const VehicleModule = namespace('vehicleModule');
const SearchQueryModule = namespace('searchQueryModule');
const InitialSettingsModule = namespace('initialSettingsModule');
const VehicleSearchModule = namespace('vehicleSearchModule');

// Custom vee-validate
Validator.extend('vehicle-customisation', {
  getMessage: (field: string) => 'Please select a vehicle',
  validate: (value: string) => validationService
    .isVehicleCustomisationDataSet()
});

@Component({
  components: {
    'form-touch-point': FormTouchPointComponent,
    'colour-touchpoint': ColourComponent,
    'vehicle-trim': VehicleTrimComponent
  }
})
export default class VehicleCustomisationComponent extends Vue {
  @VehicleModule.Action public submitDerivative;

  @VehicleModule.Action public submitDoors;

  @VehicleModule.Action public submitTransmission;

  @VehicleModule.Action public submitFuelTypes;

  @VehicleModule.Action public submitTrims;

  @VehicleModule.Getter public currentSelectionKey;

  @VehicleSearchModule.Getter public fuelTypes!: string[] | null;

  @VehicleSearchModule.Getter public transmissions!: string[] | null;

  @VehicleSearchModule.Getter public doors!: string[] | null;

  @VehicleSearchModule.Getter public derivatives!: IDerivative[] | null;

  @VehicleSearchModule.Action public updateFilteredResults;

  @SearchQueryModule.Getter public searchQuery!: SearchQuery;

  @SearchQueryModule.Action public submitBasicColour;

  @InitialSettingsModule.Getter public hideDerivativeOptions;

  @Inject('$validator') public $validator!: Validator;

  @Prop({ default: null })
  public validationScope!: string;

  @Prop({ default: 'Fuel Type' })
  public fuelTypeLabel!: string;

  @Prop({ default: 'Transmission' })
  public transmissionLabel!: string;

  @Prop({ default: 'Doors' })
  public doorsLabel!: string;

  @Prop({ default: false })
  public hideDoors!: boolean;

  @Prop({ default: 'Colour' })
  public colourTypeLabel!: string;

  @Prop({ default: false })
  public hideColourComponent!: boolean;

  @Prop({ default: () => [] })
  public questionLabels!: string[];

  @Prop({
    default: () => [
      'Not sure / Other',
      'Black',
      'Blue',
      'Red',
      'Grey',
      'White'
    ]
  })
  public colourOptions!: string;

  @Ref()
  public readonly trimComponent!: VehicleTrimComponent;

  public fuelTypeOptions: string[] | null = [];

  public transmissionOptions: string[] | null = [];

  public doorOptions: string[] | null = [];

  public get isTrim(): boolean {
    return this.searchQuery.vehicleLevel === VehicleLevel.Trim;
  }

  public get fuelTypeText() {
    return tokenService.replaceToken(this.fuelTypeLabel);
  }

  public get transmissionText() {
    return tokenService.replaceToken(this.transmissionLabel);
  }

  public get doorsText() {
    return tokenService.replaceToken(this.doorsLabel);
  }

  public get colourTypeText() {
    return tokenService.replaceToken(this.colourTypeLabel);
  }

  public get derivative(): number | null {
    return this.searchQuery.derivativeId;
  }

  public get bodystyle(): string {
    if (this.searchQuery.bodystyles && this.searchQuery.bodystyles.length > 0) {
      return this.searchQuery.bodystyles[0];
    }

    return '';
  }

  public set derivative(derivativeId: number | null) {
    let derivative: IDerivative | null = null;
    if (derivativeId !== null && this.derivatives !== null) {
      [derivative] = this.derivatives.filter((d) => d.derivativeId === derivativeId);
    }

    this.submitDerivative(derivative);
  }

  public reset() {
    this.$validator.reset();
  }

  public getValidationClass(fieldName: string) {
    return validationService.getValidationClass(this.$validator, fieldName);
  }

  public get hideDoorsTouchpoint(): boolean {
    return this.hideDoors && this.doors!.length <= 1;
  }

  public get showVehicleChoice(): boolean {
    return (!this.hideColourComponent && this.searchQuery.basicColour !== null)
      || (this.hideColourComponent && this.searchQuery.doors !== null && this.searchQuery.doors > 0);
  }

  public setFuelTypeOptions() {
    if (this.fuelTypes !== null && this.fuelTypes.length === 1) {
      this.setFuelTypeChoice(this.fuelTypes[0]);
    }

    this.fuelTypeOptions = this.fuelTypes;
  }

  public setTransmissionOptions() {
    if (this.transmissions !== null && this.transmissions.length === 1) {
      this.setTransmissionChoice(this.transmissions[0]);
    }

    this.transmissionOptions = this.transmissions;
  }

  public setDoorOptions() {
    if (this.doors !== null && this.doors.length === 1) {
      this.setDoorChoice(this.doors[0]);
    }

    this.doorOptions = this.doors;
  }

  public setFuelTypeChoice(value: string | null) {
    this.submitFuelTypes([value]);
    this.resetFuelTypeChildOptions();
    this.setTransmissionOptions();
  }

  public setTransmissionChoice(value: string | null) {
    this.submitTransmission(value);
    this.resetTransmissionChildOptions();
    this.setDoorOptions();
  }

  public setDoorChoice(value: string | null) {
    const doors = value !== null ? parseInt(value, 10) : value;
    this.submitDoors(doors);
    this.resetDoorChildOptions();
  }

  public resetFuelTypeChildOptions() {
    this.setTransmissionChoice(null);
    this.setDoorChoice(null);
    this.derivative = null;
    this.submitTrims(null);
    this.updateFilteredResults();
  }

  public resetTransmissionChildOptions() {
    this.setDoorChoice(null);
    this.derivative = null;
    this.submitTrims(null);
    this.updateFilteredResults();
  }

  public resetDoorChildOptions() {
    this.derivative = null;
    this.submitTrims(null);
    this.submitBasicColour(null);
    this.$store.dispatch('enquiryModule/submitRemoveExtraData', 'vehicleColour');
    this.updateFilteredResults();
    if (this.trimComponent !== undefined) {
      this.trimComponent.setTrimOptions();
    }
  }

  @Watch('currentSelectionKey')
  public validateSelectedOptions() {
    this.$validator.validateAll(this.validationScope);
  }

  @Watch('derivatives')
  public derivativeOptionsChanged() {
    if (!this.isTrim
      && this.derivatives !== null
      && this.derivatives.length === 1) {
      this.derivative = this.derivatives[0].derivativeId;
    }
  }

  @Watch('bodystyle')
  public bodyStylChanged() {
    this.setFuelTypeOptions();
  }

  public created() {
    this.$events.on('triggerCustomisationValidationReset', this.reset);
    this.validateSelectedOptions();
    this.setFuelTypeOptions();
  }
}
</script>
